import React, { useRef, useState } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import LoadingSequence from 'assets/Loading/LoadingSequence';
import WidgetDiagnostics from './WidgetDiagnostics';
import { useWidgetFetch } from 'hooks/useWidgetFetch';
import { apiPath } from 'apiurl';

const METADATA_URL = `${apiPath}/api/dashboard-status/metadata-counts`;
const DONUT_COLORS = ['#0d9488','#2563eb','#ea580c','#7c3aed','#16a34a','#d97706','#e11d48','#0891b2','#65a30d','#c026d3'];

function DonutColumn({ icon, iconClass, title, badge, items, centerDefault, colClass = 'pi-col' }) {
  const [hovered, setHovered] = useState(null);
  const [selected, setSelected] = useState(null);

  const top10  = items.slice(0, 10);
  const others = items.slice(10).reduce((s, x) => s + x.count, 0);
  const donutData = [
    ...top10.map((item, i) => ({ name: item.label, value: item.count, color: DONUT_COLORS[i] })),
    ...(others > 0 ? [{ name: 'Others', value: others, color: '#e2e8f0' }] : []),
  ];
  const active = hovered !== null ? hovered : selected;

  return (
    <div className={colClass}>
      <div className="pi-col-head">
        <i className={`${icon} ${iconClass}`} />
        <span>{title}</span>
        <span className="pi-badge">{badge}</span>
      </div>
      {items.length === 0 ? (
        <div className="insight-empty">No data</div>
      ) : (
        <div className="pi-inner">
          <div className="pi-list">
            {top10.map((item, i) => {
              const color    = DONUT_COLORS[i % DONUT_COLORS.length];
              const isActive = hovered === i || selected === i;
              return (
                <div
                  key={i}
                  className={`pi-list-row${i < 3 ? ' pi-list-row--top' : ''}${isActive ? ' pi-list-row--lit' : ''}`}
                  style={{ '--accent': color, cursor: 'pointer' }}
                  onClick={() => setSelected(selected === i ? null : i)}
                  onMouseEnter={() => setHovered(i)}
                  onMouseLeave={() => setHovered(null)}
                >
                  <span className="pi-list-rank">{i + 1}</span>
                  <span className="pi-list-dot" style={{ background: color }} />
                  <span className="pi-list-name" title={item.label}>{item.label}</span>
                </div>
              );
            })}
          </div>
          <div className="pi-donut-wrap" onMouseLeave={() => setHovered(null)}>
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                <Pie
                  data={donutData}
                  dataKey="value"
                  cx="50%" cy="50%"
                  innerRadius={44} outerRadius={66}
                  stroke="none" paddingAngle={2}
                  isAnimationActive={false}
                  tooltipType="none"
                >
                  {donutData.map((entry, i) => (
                    <Cell
                      key={i}
                      fill={entry.color}
                      onMouseEnter={() => setHovered(i)}
                      onClick={() => setSelected(selected === i ? null : i)}
                      style={{ cursor: 'pointer', outline: 'none', opacity: active !== null && active !== i ? 0.35 : 1 }}
                    />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="pi-donut-center">
              {active !== null && donutData[active] ? (
                <>
                  <span className="pi-donut-num" style={{ color: donutData[active].color }}>
                    {donutData[active].value.toLocaleString()}
                  </span>
                  <span className="pi-donut-sub pi-donut-sub--hover">
                    {donutData[active].name.length > 14 ? donutData[active].name.slice(0, 13) + '…' : donutData[active].name}
                  </span>
                </>
              ) : (
                <>
                  <span className="pi-donut-num">{centerDefault.value}</span>
                  <span className="pi-donut-sub">{centerDefault.label}</span>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PathogenIntelligence() {
  const infoIconRef = useRef(null);
  const [showTooltip, setShowTooltip] = useState(false);

  const { data, loading, timing } = useWidgetFetch(METADATA_URL, {
    ttl: 10 * 60 * 1000,
    swr: 30 * 60 * 1000,
    widgetId: 'Pathogen Intel',
  });

  const organisms    = data?.organisms    ?? [];
  const countries    = data?.countries    ?? [];
  const centerNames  = data?.centerNames  ?? [];
  const uniqueCounts = data?.uniqueCounts ?? {};

  const orgTotal = organisms.reduce((s, x) => s + x.count, 0) || 1;

  return (
    <div className="dash-bottom-card">
      <div className="dash-card-header">
        <span className="dash-card-title"><i className="bi bi-shield-fill-check" />Pathogen Intelligence</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div
            className="dash-tooltip-wrap"
            ref={infoIconRef}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            <i className="bi bi-info-circle dash-info-icon" />
            {showTooltip && (
              <div className="dash-tooltip-box">
                Top pathogens, sample origins, and sequencing centers from genomic surveillance metadata.
              </div>
            )}
          </div>
          <WidgetDiagnostics
            timing={timing}
            url={METADATA_URL}
            dataCount={organisms.length + countries.length + centerNames.length}
          />
        </div>
      </div>
      <div className="dash-bottom-body">
        {loading ? (
          <LoadingSequence message="Loading metadata" size="small" />
        ) : (
          <div className="pi-root">
            <DonutColumn
              icon="bi bi-bug-fill"
              iconClass="pi-icon--org"
              title="Organism"
              badge={`${uniqueCounts.organisms || organisms.length} types`}
              items={organisms}
              centerDefault={{ value: `${(orgTotal / 1000).toFixed(1)}k`, label: 'samples' }}
              colClass="pi-col"
            />
            <DonutColumn
              icon="bi bi-globe-americas"
              iconClass="pi-icon--country"
              title="Country"
              badge={uniqueCounts.countries || countries.length}
              items={countries}
              centerDefault={{ value: uniqueCounts.countries || countries.length, label: 'countries' }}
              colClass="pi-col pi-col--border"
            />
            <DonutColumn
              icon="bi bi-building"
              iconClass="pi-icon--center"
              title="Center"
              badge={uniqueCounts.centers || centerNames.length}
              items={centerNames}
              centerDefault={{ value: uniqueCounts.centers || centerNames.length, label: 'centers' }}
              colClass="pi-col pi-col--border"
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default PathogenIntelligence;
